import { useEffect, useMemo, useState } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { CourseDto, MenuItemVariantDto, MenuSectionDto } from '../../generated-api';
import { useOpeningHoursStore, useServerTimeOffsetMs } from './OpeningHoursStore';
import { isWithinOpeningHours } from '../utils/openingHours';

/** Returns a minute-granularity timestamp that updates every minute */
function useMinuteTick(): number {
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 60000));

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(Math.floor(Date.now() / 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return tick;
}

interface MenuSectionsActions {
  setMenuData: (
    sections: MenuSectionDto[],
    courses: CourseDto[],
    allSections: MenuSectionDto[],
    componentProducts: MenuItemVariantDto[],
    locationId: number
  ) => void;
  setLoading: (loading: boolean) => void;
  clear: () => void;
}

export interface MenuSectionsState {
  sections: MenuSectionDto[];
  allSections: MenuSectionDto[];
  courses: CourseDto[];
  componentProducts: MenuItemVariantDto[];
  isLoading: boolean;
  locationId: number | undefined;
  actions: MenuSectionsActions;
}

export const useMenuSectionsStore = create<MenuSectionsState>()((set) => ({
  sections: [],
  allSections: [],
  courses: [],
  isLoading: false,
  locationId: undefined,
  componentProducts: [],
  actions: {
    setMenuData: (sections, courses, allSections, componentProducts, locationId) =>
      set({ sections, courses, allSections, componentProducts, locationId, isLoading: false }),
    setLoading: (isLoading) => set({ isLoading }),
    clear: () =>
      set({
        sections: [],
        allSections: [],
        courses: [],
        componentProducts: [],
        locationId: undefined,
        isLoading: false,
      }),
  },
}));

export const useMenuCourses = () => useMenuSectionsStore((state) => state.courses);
export const useAllMenuSections = () => useMenuSectionsStore((state) => state.allSections);
export const useMenuSectionsActions = () => useMenuSectionsStore((state) => state.actions);
export const useMenuSectionsLocationId = () => useMenuSectionsStore((state) => state.locationId);
export const useMenuComponentProducts = () => useMenuSectionsStore((state) => state.componentProducts);
/**
 * Returns sections filtered by opening hours for the given timezone.
 * Sections without opening hours restrictions are always included.
 * Re-evaluates every minute to handle sections closing during a session.
 */
export const useActiveSections = (zoneId: string): MenuSectionDto[] => {
  const sections = useMenuSectionsStore((state) => state.sections);
  const openingHours = useOpeningHoursStore((state) => state.sectionOpeningHours);
  const serverTimeOffsetMs = useServerTimeOffsetMs();
  const minuteTick = useMinuteTick();

  return useMemo(() => {
    const now = new Date(Date.now() + serverTimeOffsetMs);
    return sections.filter((section) => {
      const hours = openingHours[section.sectionId];
      if (!hours || hours.length === 0) return true; // No restrictions
      return isWithinOpeningHours(hours, now, zoneId);
    });
    // minuteTick triggers re-evaluation every minute for time-sensitive opening hours
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, openingHours, zoneId, minuteTick, serverTimeOffsetMs]);
};
